// Image du résultat (victoire / défaite) à partager après le match, dessinée sur un canvas :
// aucune dépendance, fonctionne hors ligne. Partage natif (feuille de partage du téléphone)
// quand il est disponible, sinon téléchargement du PNG.

import { MatchSettings, SetRecord, TEAM_NAMES, matchResult, setsWon } from "./matchTracker";

const W = 1080;
const H = 1350;
const FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const EMOJI = "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif";

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Confettis déterministes (même image à chaque rendu).
function confetti(ctx: CanvasRenderingContext2D) {
  const colors = ["#ffd54f", "#ff8a65", "#4fc3f7", "#aed581", "#f48fb1", "#fff"];
  let seed = 7;
  const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  for (let i = 0; i < 90; i++) {
    ctx.save();
    ctx.translate(rnd() * W, rnd() * H * 0.55);
    ctx.rotate(rnd() * Math.PI);
    ctx.fillStyle = colors[i % colors.length];
    ctx.globalAlpha = 0.55 + rnd() * 0.4;
    ctx.fillRect(-8, -4, 16 + rnd() * 10, 8);
    ctx.restore();
  }
}

export async function renderResultImage(settings: MatchSettings, sets: SetRecord[], date = new Date()): Promise<Blob> {
  const winner = matchResult(sets);
  const won = setsWon(sets);
  const victory = winner === "us";
  const draw = winner === null;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas indisponible");

  // Fond
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  if (victory) {
    bg.addColorStop(0, "#0d6b3f");
    bg.addColorStop(1, "#1565a8");
  } else {
    bg.addColorStop(0, "#37474f");
    bg.addColorStop(1, "#0b2e4a");
  }
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  if (victory) confetti(ctx);

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Picto
  ctx.font = `210px ${EMOJI}`;
  ctx.fillText(victory ? "🏆" : draw ? "🤝" : "🏐", W / 2, 270);

  // Titre
  ctx.fillStyle = "#fff";
  ctx.font = `900 118px ${FONT}`;
  ctx.fillText(victory ? "VICTOIRE" : draw ? "ÉGALITÉ" : "DÉFAITE", W / 2, 500);
  ctx.font = `700 40px ${FONT}`;
  ctx.fillStyle = "rgba(255,255,255,.85)";
  ctx.fillText(victory ? "Bien joué les filles ! 🎉" : draw ? "Match arrêté à égalité" : "On reviendra plus fortes 💪", W / 2, 580);

  // Score en sets
  ctx.fillStyle = "#fff";
  ctx.font = `900 250px ${FONT}`;
  ctx.fillText(`${won.us} – ${won.them}`, W / 2, 770);
  // Noms des équipes sous leur score respectif
  ctx.font = `800 40px ${FONT}`;
  ctx.fillStyle = "rgba(255,255,255,.9)";
  ctx.textAlign = "right";
  ctx.fillText(TEAM_NAMES.us.toUpperCase(), W / 2 - 70, 905);
  ctx.textAlign = "left";
  ctx.fillText(TEAM_NAMES.them.toUpperCase(), W / 2 + 70, 905);
  ctx.textAlign = "center";

  // Détail des sets
  const n = Math.max(sets.length, 1);
  const gap = 18;
  const cw = Math.min(190, (W - 120 - gap * (n - 1)) / n);
  const x0 = (W - (cw * n + gap * (n - 1))) / 2;
  sets.forEach((r, i) => {
    const x = x0 + i * (cw + gap);
    const y = 975;
    ctx.fillStyle = r.winner === "us" ? "rgba(255,255,255,.95)" : "rgba(255,255,255,.18)";
    roundRect(ctx, x, y, cw, 118, 22);
    ctx.fill();
    ctx.fillStyle = r.winner === "us" ? "#0d6b3f" : "#fff";
    ctx.font = `700 26px ${FONT}`;
    ctx.fillText(`Set ${i + 1}`, x + cw / 2, y + 32);
    ctx.font = `900 46px ${FONT}`;
    ctx.fillText(`${r.us}–${r.them}`, x + cw / 2, y + 80);
  });

  // Bas : date + logo
  ctx.fillStyle = "rgba(255,255,255,.8)";
  ctx.font = `600 34px ${FONT}`;
  const label = date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  ctx.fillText(label.charAt(0).toUpperCase() + label.slice(1), W / 2, 1170);
  const logo = await loadImage("/logo.png");
  if (logo && logo.width > 0) {
    const h = 90;
    const w = (logo.width / logo.height) * h;
    ctx.globalAlpha = 0.95;
    ctx.drawImage(logo, (W - w) / 2, 1225, w, h);
    ctx.globalAlpha = 1;
  }

  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Export impossible"))), "image/png"));
}

export type ShareOutcome = "shared" | "downloaded";

export async function shareResultImage(settings: MatchSettings, sets: SetRecord[]): Promise<ShareOutcome> {
  const date = new Date();
  const blob = await renderResultImage(settings, sets, date);
  const name = `match-${date.toISOString().slice(0, 10)}.png`;
  const file = new File([blob], name, { type: "image/png" });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: matchResult(sets) === "us" ? "Victoire !" : "Résultat du match" });
      return "shared";
    } catch (e: any) {
      // Feuille de partage fermée par l'utilisateur : rien à faire. Autre erreur : on télécharge.
      if (e?.name === "AbortError") return "shared";
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return "downloaded";
}
